import {
    getPartCompletion,
    getPartMaterialId,
    getSelectedPartColor,
} from "./designerState";
import {
    filterMaterialsForPart,
    shouldHideExtraPartByName,
    shouldHideDesignerPart,
    isStripePartNode,
} from "./designerHelpers";
import { MASTER_PART_TYPE } from "./designerUtils";
import {
    buildPartGroupsFromTree,
} from "./designerTreeUtils";

export const selectPartCards = ({
    partNodes = [],
    treeContext = {},
    state,
    allColors = [],
    includeStripeParts = false,
}) =>
    partNodes
        .map((partItem) => {
            const key = partItem.key;
            const node = partItem.node;
            const parentNode = partItem.parentKey ? treeContext.nodeByKey?.[partItem.parentKey] : null;
            const groupId = treeContext.keyToExclusiveGroup?.[key] || null;
            const group = groupId ? treeContext.exclusiveGroups?.[groupId] : null;
            const isExclusiveVariant = !!group;
            const isExclusiveRequiredGroup = !!(group && group.requiredKeys?.size > 0);
            const selectedInGroup = isExclusiveVariant
                ? group.keys.filter((candidate) => state.selectedKeys.has(String(candidate)))
                : [];
            const baseCompletion = getPartCompletion({ node, partKey: key, state, allColors });
            const completion = (!state.selectedKeys.has(key) && isExclusiveRequiredGroup)
                ? { selected: false, complete: true, missing: [] }
                : baseCompletion;
            return {
                key,
                label: node?.Item?.nname || node?.Item?.name || "Parte",
                parentLabel: parentNode?.Item?.nname || parentNode?.Item?.name || null,
                parentKey: partItem.parentKey || null,
                type: Number(node?.type),
                required: !!node?.required && !isExclusiveRequiredGroup,
                isExclusiveVariant,
                isExclusiveRequiredGroup,
                exclusiveGroupId: groupId,
                selectedInExclusiveGroup: selectedInGroup.map(String),
                selected: state.selectedKeys.has(key),
                complete: completion.complete,
                missing: completion.missing,
                node,
            };
        })
        .filter((part) =>
            !shouldHideExtraPartByName(part.node)
            && (includeStripeParts || !isStripePartNode(part.node))
            && !shouldHideDesignerPart({
                groupLabel: part.parentLabel,
                partLabel: part.label,
                node: part.node,
            })
        );

export const selectPartMaterialCards = ({
    partCards = [],
    state,
    expandedPalettes = {},
    loadingColors = {},
    loadingMaterialColors = {},
    colorOptions = {},
    materialResolutionByPart = {},
}) => {
    const cardsByKey = Object.fromEntries(partCards.map((part) => [String(part.key), part]));
    return partCards.map((part) => {
        const node = part.node;
        const sourceMaterials = Array.isArray(node?.materials) ? node.materials : Object.values(node?.materials || {});
        const materials = filterMaterialsForPart(part.label, sourceMaterials);
        const isLeftRightPart = [Number(MASTER_PART_TYPE.PART_LEFT), Number(MASTER_PART_TYPE.PART_RIGHT)].includes(Number(part.type));
        const siblingType = Number(part.type) === Number(MASTER_PART_TYPE.PART_LEFT)
            ? Number(MASTER_PART_TYPE.PART_RIGHT)
            : Number(MASTER_PART_TYPE.PART_LEFT);
        const pairedPart = isLeftRightPart
            ? partCards.find((candidate) =>
                String(candidate.key) !== String(part.key)
                && String(candidate.parentKey || "") === String(part.parentKey || "")
                && Number(candidate.type) === siblingType
            )
            : null;
        const pairedPartCard = pairedPart ? cardsByKey[String(pairedPart.key)] || pairedPart : null;
        const selectedMaterialIdRaw = node ? getPartMaterialId(state, node, part.key) : null;
        const selectedMaterialValid = materials.some(
            (material) => String(material.id) === String(selectedMaterialIdRaw)
        );
        const selectedMaterialId = selectedMaterialValid
            ? String(selectedMaterialIdRaw)
            : null;
        const selectedColor = selectedMaterialId ? getSelectedPartColor(state, part.key, selectedMaterialId) : null;
        const paletteKey = selectedMaterialId ? `${part.key}-${selectedMaterialId}` : null;
        const colorsKnownForSelected = selectedMaterialId
            ? Object.prototype.hasOwnProperty.call(colorOptions, selectedMaterialId)
            : false;
        const availableColors = selectedMaterialId ? colorOptions[selectedMaterialId] || [] : [];
        const selectedMaterialHasColors = availableColors.length > 0;
        const materialResolution = materialResolutionByPart[String(part.key)] || null;
        const showMaterialSelector = false;
        const isAutoMaterialResolving = Boolean(
            part.selected
            && materials.length > 0
            && !selectedMaterialId
            && !materialResolution?.checked
        );
        const requiresManualMaterialSelection = Boolean(
            part.selected
            && materials.length > 0
            && materialResolution?.checked
            && (
                !selectedMaterialId
                || !materialResolution.hasMaterialWithColors
                || (colorsKnownForSelected && !selectedMaterialHasColors)
            )
        );
        const requiresManualColorSelection = Boolean(
            part.selected
            && selectedMaterialId
            && colorsKnownForSelected
            && selectedMaterialHasColors
            && !selectedColor
        );
        const hasSelectableColorPalette = Boolean(
            part.selected
            && selectedMaterialId
            && colorsKnownForSelected
            && selectedMaterialHasColors
        );
        const showMaterialFeedback =
            hasSelectableColorPalette
            || requiresManualMaterialSelection
            || requiresManualColorSelection;

        return {
            ...part,
            materialOptions: materials,
            isLeftRightPart,
            pairedPartKey: pairedPart ? String(pairedPart.key) : null,
            pairedPartLabel: pairedPart?.label || null,
            pairedPartSelected: !!pairedPartCard?.selected,
            pairGroupKey: pairedPart ? `${String(part.parentKey || "root")}:left-right` : null,
            selectedMaterialId,
            selectedColor,
            paletteKey,
            paletteLoaded: selectedMaterialId
                ? Object.prototype.hasOwnProperty.call(colorOptions, selectedMaterialId)
                : false,
            paletteOpen: paletteKey ? !!expandedPalettes[paletteKey] : false,
            paletteLoading: Boolean(
                (paletteKey ? !!loadingColors[paletteKey] : false)
                || (selectedMaterialId ? !!loadingMaterialColors[selectedMaterialId] : false)
            ),
            availableColors,
            showMaterialSelector,
            isAutoMaterialResolving,
            requiresManualMaterialSelection,
            requiresManualColorSelection,
            showMaterialFeedback,
            canSublimate: false,
            isSublimated: false,
            selectedFullprint: null,
        };
    });
};

export const selectPartMaterialCardByKey = (partMaterialCards = []) =>
    Object.fromEntries(partMaterialCards.map((part) => [String(part.key), part]));

export const selectPartGroups = ({
    normalizedRoot,
    selectedKeys,
    activePartKey,
    partMaterialCardByKey,
    hideStripeParts = true,
}) =>
    buildPartGroupsFromTree({
        root: normalizedRoot,
        selectedKeys,
        cardByKey: partMaterialCardByKey,
        activePartKey,
        hideStripeParts,
    });

export const selectResolvedActivePartKey = ({
    activePartKey,
    partGroups = [],
}) => {
    const rawActivePartKey = String(activePartKey || "");
    if (!rawActivePartKey) return null;

    for (const group of partGroups) {
        const options = group?.options || [];
        if (options.some((option) => String(option.key) === rawActivePartKey)) {
            return rawActivePartKey;
        }
        if (String(group?.sourceKey || "") === rawActivePartKey) {
            return group?.activeOptionKey || rawActivePartKey;
        }
        const matchedOption = options.find((option) =>
            Array.isArray(option?.subtreeKeys) && option.subtreeKeys.includes(rawActivePartKey)
        );
        if (matchedOption) {
            return String(matchedOption.key);
        }
    }

    return rawActivePartKey;
};

export const selectActivePartPrintLocations = ({
    allPrintLocations = [],
    resolvedActivePartKey,
}) => {
    const activePartKey = String(resolvedActivePartKey || "");
    if (!activePartKey) return [];
    return allPrintLocations.filter((location) => String(location.partKey) === activePartKey);
};

export const selectStep2PrintLocations = ({
    allPrintLocations = [],
    printHotspotFilter,
    resolvedActivePartKey = "",
}) => {
    const activePartKey = String(resolvedActivePartKey || "").trim();
    if (!printHotspotFilter?.printKey && activePartKey) {
        const activePartLocations = allPrintLocations.filter(
            (location) => String(location.partKey) === activePartKey
        );
        if (activePartLocations.length > 0) {
            return activePartLocations;
        }
    }
    if (!printHotspotFilter?.printKey) return allPrintLocations;
    return allPrintLocations.filter(
        (location) => String(location.key) === String(printHotspotFilter.printKey)
    );
};
